import React, { useContext, useEffect, useRef } from 'react';
import { MobXProviderContext, observer } from 'mobx-react';
import scoringService from '../../services/ScoringService';
import { LessonMode } from '../../../Constants';
import UtilsService from '../../services/UtilsService';
import HtmlComponent from '../HtmlComponent';
import { register } from '../../../i18n';
import { isRubricCategoryFullyChecked, isRubricCheckboxScoring } from '../../utils/rubricCheckboxScoring';

const t = register('PanelMenu');
const t2 = register('AriaLabels');

const RubricScoreTable = observer(({ lessonElementId, onScoreChange } = {}) => {
  const {
    lessonManager,
    responseManager,
    scoringManager
  } = useContext(MobXProviderContext);

  const lessonElementState = responseManager.getLessonElementState(lessonElementId);
  const rubric = scoringManager.rubricMap.get(lessonElementId);
  const mode = lessonManager.playerMode;
  const checkboxMode = rubric && isRubricCheckboxScoring(rubric);
  const teacherScoreRef = useRef();

  const handleChange = (event, categoryId) => {
    const str = event.target.value;
    if (UtilsService.ensureNumericInput(str)) {
      scoringService.setCategoryTempScoreFromTable(lessonElementId, categoryId, str);

      // Notify parent that score changed
      if (onScoreChange) {
        onScoreChange();
      }
    }
  };

  const handleBlur = (event, categoryId, maxPoints) => {
    let str = event.target.value;

    if (UtilsService.isNullOrEmpty(str) || str.trim() === '.') {
      scoringService.setCategoryTempScoreFromTableOnBlur(lessonElementId, categoryId);
      return;
    }
    if (UtilsService.ensureNumericInput(str)) {
      const num = parseFloat(str);
      const maxScore = parseFloat(maxPoints);
      if (num > maxScore) {
        str = maxScore.toFixed(2);
      }
      if (num < 0) {
        str = '0.00';
      }
      scoringService.setCategoryTempScoreFromTable(lessonElementId, categoryId, str);
    }
  };

  const fieldValue = (category) => {
    if (typeof category.calculatedTempScore !== 'undefined') {
      return category.calculatedTempScore;
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (lessonElementState && lessonElementState.subscore) {
      scoringManager.setSubscore(lessonElementId, JSON.parse(lessonElementState.subscore), lessonElementState.isScored);
    } else {
      if (teacherScoreRef && teacherScoreRef.current) {
        teacherScoreRef.current.focus();
      }
    }
  }, []);

  return (
    <>
      <table className='rubric-score-table'>
        <tbody>
          {rubric.categories.map((category, idx) => {
            const extraProps = idx === 0 ? { ref: teacherScoreRef } : {}; // set ref for first score box in order to preselect
            return (
              <tr key={`${category.categoryId}_scoring-table`} className='rubric-score-row'>
                <td className='category-name'>
                  <HtmlComponent htmlStr={category.name} />
                </td>
                <td className='category-score'>
                  {checkboxMode ? (
                    <>
                      {(mode === LessonMode.SCORING) ? (
                        <input
                          aria-label={t2('rubricCategoryMetCheckbox')}
                          checked={isRubricCategoryFullyChecked(category)}
                          onChange={(e) => {
                            scoringService.setCategoryCheckboxScore(
                              lessonElementId,
                              category.categoryId,
                              e.target.checked
                            );
                            if (onScoreChange) {
                              onScoreChange();
                            }
                          }}
                          type='checkbox'
                          {...extraProps}
                        />
                      ) : (
                        <input
                          aria-label={t2('rubricCategoryMetCheckbox')}
                          checked={isRubricCategoryFullyChecked(category)}
                          disabled
                          type='checkbox'
                        />
                      )}
                    </>
                  ) : (
                    <>
                      {(mode === LessonMode.SCORING) && (
                        <input
                          aria-label={t2('categoryfinalscore')}
                          onBlur={(e) => {
                            handleBlur(e, category.categoryId, category.maxPoints);
                          }}
                          onChange={(e) => {
                            handleChange(e, category.categoryId);
                          }}
                          type='text'
                          value={fieldValue(category)}
                          {...extraProps} />
                      )}
                      {(mode === LessonMode.REVIEW) && <span>{(category.calculatedTempScore || '0.00')}</span>}
                      <span>/</span><span>{category.maxPoints}</span>
                    </>
                  )}
                </td>
              </tr>
            );
          })}

        </tbody>
      </table>
      {!checkboxMode && (
        <div className='total-box'>
          <div className='category-name'>{t('total')}:</div>
          {(mode === LessonMode.SCORING) && (
            <div className='category-score'>
              <span>{scoringManager.tempScores.get(lessonElementId) || '0.00'}</span><span> /</span> <span>{lessonElementState.maxScore}</span>
            </div>
          )}
          {(mode === LessonMode.REVIEW) && (
            <div className='category-score review'>
              <span>{lessonElementState.submittedItemScore || '0.00'}</span><span>/</span><span>{lessonElementState.maxScore}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
});
export default RubricScoreTable;
